/**
 * CallControls - Control bar for video/audio calls
 * Mute, video toggle, participants, invite, end call buttons
 */

import React from 'react';
import { useTranslation } from 'react-i18next';
import './VideoCall.css';

function CallControls({
    callType,
    isVideoEnabled,
    isAudioEnabled,
    isScreenSharing,
    isHandRaised,
    onToggleVideo,
    onToggleAudio,
    onToggleScreenShare,
    onToggleHandRaise,
    onEndCall,
    onToggleParticipants,
    onInvite,
    onToggleDeviceSettings,
    onToggleFullscreen,
    isFullscreen = false,
    deviceSettingsOpen = false,
    participantsOpen,
    participantCount = 0
}) {
    const { t } = useTranslation();

    return (
        <div className="call-controls">
            {/* Mute/Unmute Audio */}
            <button
                className={`control-btn ${!isAudioEnabled ? 'control-btn-off' : ''}`}
                onClick={onToggleAudio}
                title={isAudioEnabled ? t('aiChat.calls.mute') : t('aiChat.calls.unmute')}
            >
                {isAudioEnabled ? (
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z" />
                        <path d="M19 10v2a7 7 0 0 1-14 0v-2" />
                        <line x1="12" y1="19" x2="12" y2="23" />
                        <line x1="8" y1="23" x2="16" y2="23" />
                    </svg>
                ) : (
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <line x1="1" y1="1" x2="23" y2="23" />
                        <path d="M9 9v3a3 3 0 0 0 5.12 2.12M15 9.34V4a3 3 0 0 0-5.94-.6" />
                        <path d="M17 16.95A7 7 0 0 1 5 12v-2m14 0v2a7 7 0 0 1-.11 1.23" />
                        <line x1="12" y1="19" x2="12" y2="23" />
                        <line x1="8" y1="23" x2="16" y2="23" />
                    </svg>
                )}
            </button>

            {/* Toggle Video (only for video calls) */}
            {callType === 'video' && (
                <button
                    className={`control-btn ${!isVideoEnabled ? 'control-btn-off' : ''}`}
                    onClick={onToggleVideo}
                    title={isVideoEnabled ? t('aiChat.calls.turnOffCamera') : t('aiChat.calls.turnOnCamera')}
                >
                    {isVideoEnabled ? (
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M23 7l-7 5 7 5V7z" />
                            <rect x="1" y="5" width="15" height="14" rx="2" ry="2" />
                        </svg>
                    ) : (
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M16 16v1a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V7a2 2 0 0 1 2-2h2m5.66 0H14a2 2 0 0 1 2 2v3.34l1 1L23 7v10" />
                            <line x1="1" y1="1" x2="23" y2="23" />
                        </svg>
                    )}
                </button>
            )}

            {/* Screen Share Button */}
            <button
                className={`control-btn ${isScreenSharing ? 'control-btn-active' : ''}`}
                onClick={onToggleScreenShare}
                title={isScreenSharing ? t('aiChat.clara.stopSharing') : t('aiChat.calls.shareScreen')}
            >
                {isScreenSharing ? (
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <rect x="2" y="3" width="20" height="14" rx="2" ry="2" />
                        <line x1="8" y1="21" x2="16" y2="21" />
                        <line x1="12" y1="17" x2="12" y2="21" />
                        <line x1="2" y1="3" x2="22" y2="17" />
                    </svg>
                ) : (
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <rect x="2" y="3" width="20" height="14" rx="2" ry="2" />
                        <line x1="8" y1="21" x2="16" y2="21" />
                        <line x1="12" y1="17" x2="12" y2="21" />
                    </svg>
                )}
            </button>

            {/* Raise Hand Button - only where hand raising is actually wired up */}
            {onToggleHandRaise && (
                <button
                    className={`control-btn ${isHandRaised ? 'control-btn-hand-raised' : ''}`}
                    onClick={onToggleHandRaise}
                    title={isHandRaised ? t('aiChat.calls.lowerHand') : t('aiChat.calls.raiseHand')}
                >
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M18 11V6a1 1 0 0 0-2 0v4M15 10V4a1 1 0 0 0-2 0v6M12 10V3a1 1 0 0 0-2 0v7M9 11V7a1 1 0 0 0-2 0v7.17A6.5 6.5 0 0 0 7 20.5" />
                        <path d="M18 11a2 2 0 0 1 2 2v1.5A6.5 6.5 0 0 1 13.5 21h-1A6.5 6.5 0 0 1 6 14.5" />
                    </svg>
                </button>
            )}

            {/* Camera / microphone / speaker pickers.
                The wrong headset is the most common way a bridge loses its first two minutes,
                and until now the only fix was to leave the call and change the OS default. */}
            {onToggleDeviceSettings && (
                <button
                    className={`control-btn ${deviceSettingsOpen ? 'control-btn-active' : ''}`}
                    onClick={onToggleDeviceSettings}
                    title={t('aiChat.calls.deviceSettings')}
                >
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <circle cx="12" cy="12" r="3" />
                        <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" />
                    </svg>
                </button>
            )}

            {/* Full screen. Only rendered where the browser will actually grant it - a
                button that quietly does nothing is worse than one that is not there. */}
            {onToggleFullscreen && (
                <button
                    className={`control-btn ${isFullscreen ? 'control-btn-active' : ''}`}
                    onClick={onToggleFullscreen}
                    title={isFullscreen ? t('aiChat.calls.exitFullScreen') : t('aiChat.calls.fullScreen')}
                >
                    {isFullscreen ? (
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M8 3v3a2 2 0 0 1-2 2H3M21 8h-3a2 2 0 0 1-2-2V3M3 16h3a2 2 0 0 1 2 2v3M16 21v-3a2 2 0 0 1 2-2h3" />
                        </svg>
                    ) : (
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M8 3H5a2 2 0 0 0-2 2v3M21 8V5a2 2 0 0 0-2-2h-3M3 16v3a2 2 0 0 0 2 2h3M16 21h3a2 2 0 0 0 2-2v-3" />
                        </svg>
                    )}
                </button>
            )}

            {/* Participants Button */}
            <button
                className={`control-btn ${participantsOpen ? 'control-btn-active' : ''}`}
                onClick={onToggleParticipants}
                title={t('aiChat.calls.participants')}
            >
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
                    <circle cx="9" cy="7" r="4" />
                    <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
                    <path d="M16 3.13a4 4 0 0 1 0 7.75" />
                </svg>
                {participantCount > 0 && (
                    <span className="control-btn-badge">{participantCount}</span>
                )}
            </button>

            {/* Invite Button */}
            <button
                className="control-btn control-btn-invite"
                onClick={onInvite}
                title={t('aiChat.calls.inviteOthers')}
            >
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M16 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
                    <circle cx="8.5" cy="7" r="4" />
                    <line x1="20" y1="8" x2="20" y2="14" />
                    <line x1="23" y1="11" x2="17" y2="11" />
                </svg>
            </button>

            {/* End Call */}
            <button
                className="control-btn control-btn-end"
                onClick={onEndCall}
                title={t('aiChat.calls.endCall')}
            >
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M10.68 13.31a16 16 0 0 0 3.41 2.6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7 2 2 0 0 1 1.72 2v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.42 19.42 0 0 1-3.33-2.67m-2.67-3.34a19.79 19.79 0 0 1-3.07-8.63A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91" />
                    <line x1="1" y1="1" x2="23" y2="23" />
                </svg>
            </button>
        </div>
    );
}

export default CallControls;
